const pageHeadConfig = require("../data/pageHeadConfig.json");

function escapeHtmlAttribute(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeJsonForScript(json) {
  return JSON.stringify(json)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
}

function normalizeMetaContent(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ");
  }

  if (value === null || typeof value === "undefined") {
    return "";
  }

  return String(value);
}

function getSharedHead() {
  return (pageHeadConfig && pageHeadConfig.shared) || {};
}

function resolveMetaTemplateValue(value, context = {}) {
  if (typeof value !== "string") return value;

  return value
    .replace(/\{\{\s*title\s*\}\}/gi, context.title || "")
    .replace(/\{\{\s*description\s*\}\}/gi, context.description || "")
    .replace(/\{\{\s*(canonical|canonicalUrl|url)\s*\}\}/gi, context.canonical || "");
}

function resolveMetaTemplates(meta = {}, context = {}) {
  return Object.entries(meta).reduce((resolvedMeta, [key, value]) => {
    resolvedMeta[key] = resolveMetaTemplateValue(value, context);
    return resolvedMeta;
  }, {});
}

function mergeHeadWithShared(pageHead = {}) {
  const sharedHead = getSharedHead();

  return {
    ...sharedHead,
    ...pageHead,
    meta: {
      ...(sharedHead.meta || {}),
      ...(pageHead.meta || {}),
    },
  };
}

function getMetaTags(head = {}) {
  const entries = Object.entries(head.meta || {});

  return entries
    .map(([key, rawValue]) => {
      const content = normalizeMetaContent(rawValue);
      if (!content) return null;

      return {
        attributeName: key.includes(":") ? "property" : "name",
        attributeValue: key,
        content,
      };
    })
    .filter(Boolean);
}

function getAllManagedMetaKeys() {
  const keys = new Set();

  getMetaTags(getSharedHead()).forEach((tag) => {
    keys.add(`${tag.attributeName}::${tag.attributeValue}`);
  });

  Object.values(pageHeadConfig.pages || {}).forEach((page) => {
    getMetaTags(mergeHeadWithShared(page.head || {})).forEach((tag) => {
      keys.add(`${tag.attributeName}::${tag.attributeValue}`);
    });
  });

  return keys;
}

function getMetaElements(head = {}) {
  const allTags = getMetaTags(head);
  const robotTag = allTags.find((tag) => (
    tag.attributeName === "name" && tag.attributeValue.toLowerCase() === "robots"
  ));
  const ogOrder = ["og:title", "og:description", "og:url", "og:type", "og:site_name"];
  const ogTags = ogOrder
    .map((key) => allTags.find((tag) => (
      tag.attributeName === "property" && tag.attributeValue.toLowerCase() === key
    )))
    .filter(Boolean);

  const consumedTagKeys = new Set([
    ...(robotTag ? [`${robotTag.attributeName}::${robotTag.attributeValue}`] : []),
    ...ogTags.map((tag) => `${tag.attributeName}::${tag.attributeValue}`),
  ]);

  const remainingTags = allTags.filter((tag) => (
    !consumedTagKeys.has(`${tag.attributeName}::${tag.attributeValue}`)
  ));

  const elements = [];

  if (robotTag) {
    elements.push("<!-- Standard indexing -->");
    elements.push(
      `<meta ${robotTag.attributeName}="${escapeHtmlAttribute(robotTag.attributeValue)}" content="${escapeHtmlAttribute(robotTag.content)}">`
    );
  }

  if (ogTags.length) {
    elements.push("<!-- Open Graph -->");
    ogTags.forEach((tag) => {
      elements.push(
        `<meta ${tag.attributeName}="${escapeHtmlAttribute(tag.attributeValue)}" content="${escapeHtmlAttribute(tag.content)}">`
      );
    });
  }

  remainingTags.forEach((tag) => {
    elements.push(
      `<meta ${tag.attributeName}="${escapeHtmlAttribute(tag.attributeValue)}" content="${escapeHtmlAttribute(tag.content)}">`
    );
  });

  elements.push("<!-- Confirm robots.txt allows these crawlers: -->");
  elements.push("<!-- GPTBot, ClaudeBot, PerplexityBot, anthropic-ai, cohere-ai -->");

  return elements;
}

function normalizeCanonicalPath(canonicalPath) {
  if (!canonicalPath) return "";

  const path = normalizePathname(canonicalPath);
  if (!path.startsWith("/")) return `/${path}`;
  return path;
}

function resolveCanonicalHref(canonicalPath, currentOrigin) {
  const normalizedCanonicalPath = normalizeCanonicalPath(canonicalPath);
  if (!normalizedCanonicalPath) return "";

  if (!currentOrigin) return normalizedCanonicalPath;

  return `${String(currentOrigin).replace(/\/$/, "")}${normalizedCanonicalPath}`;
}

function getLinkElements(head = {}, options = {}) {
  const canonicalHref = resolveCanonicalHref(head.canonical, options.currentOrigin);
  if (!canonicalHref) return [];

  return [
    `<link rel="canonical" href="${escapeHtmlAttribute(canonicalHref)}">`,
  ];
}

function normalizeSchemaItems(schema) {
  if (!schema) return [];
  if (Array.isArray(schema)) return schema.filter(Boolean);
  return [schema];
}

function resolveSchemaUrls(value, currentOrigin, key) {
  if (Array.isArray(value)) {
    return value.map((item) => resolveSchemaUrls(item, currentOrigin));
  }

  if (!value || typeof value !== "object") {
    if (
      typeof value === "string"
      && currentOrigin
      && value.startsWith("/")
      && ["url", "@id", "mainEntityOfPage"].includes(key)
    ) {
      return `${String(currentOrigin).replace(/\/$/, "")}${value}`;
    }

    return value;
  }

  return Object.keys(value).reduce((acc, childKey) => {
    acc[childKey] = resolveSchemaUrls(value[childKey], currentOrigin, childKey);
    return acc;
  }, {});
}

function getSchemaItems(head = {}, options = {}) {
  return normalizeSchemaItems(head.schema)
    .map((item) => resolveSchemaUrls(item, options.currentOrigin))
    .filter(Boolean);
}

function getSchemaElements(head = {}, options = {}) {
  return getSchemaItems(head, options).map((schemaItem) => (
    `<script type="application/ld+json">${escapeJsonForScript(schemaItem)}</script>`
  ));
}

function normalizePathname(pathname) {
  if (!pathname) return "/";

  const withoutQuery = pathname.split("?")[0].split("#")[0];
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }

  return withoutQuery || "/";
}

function pathMatchesRoutePattern(routePath, pathname) {
  const normalizedRoute = normalizePathname(routePath);
  const normalizedPathname = normalizePathname(pathname);

  if (normalizedRoute === normalizedPathname) return true;

  const routeSegments = normalizedRoute.split("/").filter(Boolean);
  const pathSegments = normalizedPathname.split("/").filter(Boolean);

  if (routeSegments.length !== pathSegments.length) return false;

  return routeSegments.every((routeSegment, index) => {
    if (routeSegment.startsWith(":")) return true;
    return routeSegment === pathSegments[index];
  });
}

function interpolateRouteParams(templatePath, routePath, pathname) {
  if (!templatePath) return "";

  const normalizedTemplatePath = normalizePathname(templatePath);
  if (!normalizedTemplatePath.includes(":")) return normalizedTemplatePath;

  const normalizedRoute = normalizePathname(routePath);
  const normalizedPathname = normalizePathname(pathname);
  const routeSegments = normalizedRoute.split("/").filter(Boolean);
  const pathSegments = normalizedPathname.split("/").filter(Boolean);

  if (routeSegments.length !== pathSegments.length) {
    return normalizedTemplatePath;
  }

  const params = routeSegments.reduce((acc, routeSegment, index) => {
    if (routeSegment.startsWith(":")) {
      acc[routeSegment.slice(1)] = pathSegments[index];
    }
    return acc;
  }, {});

  const resolvedSegments = normalizedTemplatePath
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      if (!segment.startsWith(":")) return segment;
      const paramName = segment.slice(1);
      return params[paramName] || segment;
    });

  return `/${resolvedSegments.join("/")}`;
}

function getPageHeadEntryByPath(pathname) {
  const normalizedPathname = normalizePathname(pathname);

  return Object.values(pageHeadConfig.pages).find((page) => (
    pathMatchesRoutePattern(page.route.path, normalizedPathname)
  )) || null;
}

function getStaticSharedMeta() {
  const sharedHead = getSharedHead();
  const staticMeta = {};
  
  Object.entries(sharedHead.meta || {}).forEach(([key, value]) => {
    // Keep only non-templated meta in shared block
    if (!String(value).includes('{{')) {
      staticMeta[key] = value;
    }
  });
  
  return staticMeta;
}

function getPageHeadByPath(pathname, options = {}) {
  const page = getPageHeadEntryByPath(pathname);
  const pageHead = page && page.head ? page.head : {};
  const resolvedCanonicalPath = interpolateRouteParams(
    pageHead.canonical,
    page && page.route ? page.route.path : "",
    pathname,
  );
  
  let mergedHead;
  if (page) {
    // For known pages: merge shared + page-specific meta
    mergedHead = mergeHeadWithShared(pageHead);
  } else {
    // For unknown pages: use only static shared meta + page meta
    mergedHead = {
      ...pageHead,
      meta: {
        ...getStaticSharedMeta(),
        ...(pageHead.meta || {}),
      },
    };
  }
  
  const resolvedHead = {
    ...mergedHead,
    canonical: resolvedCanonicalPath,
  };
  const canonicalHref = resolveCanonicalHref(resolvedHead.canonical, options.currentOrigin);
  
  const resolvedMeta = page ? resolveMetaTemplates(resolvedHead.meta, {
    title: resolvedHead.title || "",
    description: resolvedHead.meta && resolvedHead.meta.description ? resolvedHead.meta.description : "",
    canonical: canonicalHref,
  }) : resolvedHead.meta;
  
  const finalHead = {
    ...resolvedHead,
    meta: resolvedMeta,
  };

  return {
    hasPageHead: Boolean(page),
    title: finalHead.title || "Spacer by CBRE",
    canonical: canonicalHref,
    metaTags: getMetaTags(finalHead),
    metaElements: getMetaElements(finalHead),
    linkElements: getLinkElements(finalHead, options),
    schemaItems: getSchemaItems(finalHead, options),
    schemaElements: getSchemaElements(finalHead, options),
  };
}

const pageTitleMap = Object.values(pageHeadConfig.pages || {}).reduce((map, page) => {
  if (page.route?.path && page.head?.title) {
    map[page.route.path] = page.head.title;
  }
  return map;
}, {});

module.exports = {
  normalizePathname,
  normalizeCanonicalPath,
  resolveCanonicalHref,
  getMetaTags,
  getAllManagedMetaKeys,
  getMetaElements,
  getLinkElements,
  getSchemaItems,
  getSchemaElements,
  pathMatchesRoutePattern,
  interpolateRouteParams,
  getPageHeadEntryByPath,
  getPageHeadByPath,
  pageHeadConfig,
  pageTitleMap,
};